import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API_BASE } from "@/App";
import {
  PenTool, BookOpen, Landmark, TrendingUp, Beaker, Globe,
  Heart, Flame, Sparkles, Trophy, Target, Clock, Anchor,
  ChevronRight, ArrowRight, BarChart3, Calendar, Scale, FileText, Languages
} from "lucide-react";

const PAPER_CARDS = [
  { id: "paper1", name: "General Essay", icon: PenTool, color: "from-rose-500 to-pink-600", marks: 150 },
  { id: "paper2", name: "History, Culture & Geography", icon: BookOpen, color: "from-amber-500 to-orange-600", marks: 150 },
  { id: "paper3", name: "Polity, Governance, Law & Ethics", icon: Scale, color: "from-blue-500 to-indigo-600", marks: 150 },
  { id: "paper4", name: "Economy & Development", icon: TrendingUp, color: "from-emerald-500 to-teal-600", marks: 150 },
  { id: "paper5", name: "Science & Technology", icon: Beaker, color: "from-cyan-500 to-blue-600", marks: 150 },
  { id: "english", name: "English Language", icon: FileText, color: "from-violet-500 to-purple-600", marks: 150 },
  { id: "telugu", name: "Telugu Language", icon: Languages, color: "from-pink-500 to-rose-600", marks: 150 },
];

const APPSCDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [streakData, setStreakData] = useState({ current_streak: 0, longest_streak: 0 });

  const firstName = user?.name?.split(" ")[0] || "Aspirant";
  const examDate = new Date("2026-08-20");
  const daysLeft = Math.max(0, Math.ceil((examDate - new Date()) / (1000 * 60 * 60 * 24)));

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/command/dashboard-all?exam_type=APPSC`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.streak) setStreakData(data.streak);
        }
      } catch (e) { if (!isCancelError(e)) {} }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-blue-200 text-sm">{getGreeting()}</p>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Anchor className="w-6 h-6" /> {firstName}'s APPSC Command
              </h1>
              <p className="text-blue-200 text-xs mt-0.5">Andhra Pradesh Group-1 Mains — Answer Writing Focus</p>
            </div>
            <div className="text-center bg-white/10 rounded-2xl px-5 py-3">
              <p className="text-3xl font-bold">{daysLeft}</p>
              <p className="text-[10px] text-blue-200">days to exam</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{streakData.current_streak}</p>
              <p className="text-[10px] text-blue-200">Day Streak</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-xl font-bold">7</p>
              <p className="text-[10px] text-blue-200">Mains Papers</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-xl font-bold">1050</p>
              <p className="text-[10px] text-blue-200">Total Marks</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Mains CTA */}
        <button onClick={() => navigate("/appsc/mains")} className="w-full text-left group" data-testid="appsc-mains-hub-btn">
          <Card className="border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 hover:shadow-lg transition-all group-hover:border-blue-400">
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <PenTool className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Mains Answer Writing Hub</h2>
                  <p className="text-sm text-slate-600">Practice all 7 papers with AI evaluation</p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-blue-500 group-hover:translate-x-1 transition-transform" />
            </CardContent>
          </Card>
        </button>

        {/* Paper Quick Access */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick Access — Papers</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PAPER_CARDS.map((p) => (
              <button key={p.id} onClick={() => navigate(`/appsc/paper/${p.id}`)}
                className="text-left group" data-testid={`quick-${p.id}`}>
                <Card className="h-full hover:shadow-md transition-all border-slate-200 hover:border-blue-300 group-hover:scale-[1.02]">
                  <CardContent className="p-3">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center mb-2`}>
                      <p.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="font-medium text-slate-800 text-xs leading-tight">{p.name}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{p.marks}M</p>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        </div>

        {/* Cross-Platform Features */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">More Tools</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { title: "Answer Arena", icon: PenTool, path: "/answer-arena", color: "text-indigo-500" },
              { title: "National Rank", icon: Trophy, path: "/national-benchmark", color: "text-amber-500" },
              { title: "Daily Intelligence", icon: Sparkles, path: "/daily-intelligence", color: "text-rose-500" },
            ].map((f) => (
              <button key={f.title} onClick={() => navigate(f.path)}
                className="flex items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all text-left"
                data-testid={`tool-${f.title.toLowerCase().replace(/\s/g, '-')}`}>
                <f.icon className={`w-4 h-4 ${f.color}`} />
                <span className="text-xs font-medium text-slate-700">{f.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Exam Info */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">APPSC Group-1 (2026)</p>
            <p className="text-sm text-slate-600">Screening Test (150Q MCQ) + Mains (7 Descriptive Papers, 1050 Marks) + Interview (75 Marks). Focus on AP-specific content in every paper.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default APPSCDashboardPage;
